import type { TemplateName } from '@quromedical/communication/server';
import { EncounterService } from '@quromedical/fhir-common';
import { flatten } from '@quromedical/utils';

import { compose, resolveConstant, type SuccessResolvers } from '../composer';

export type CommunicationParams = {
  dayOfAdmission: number;
  admissionService: EncounterService;
};

type Days = `${number}` | `${number}-${number}`;
export type MessageRecords = Record<Days, TemplateName | undefined>;

export type Communication = Record<EncounterService, MessageRecords>;

export const createMassageRecords = (data: MessageRecords): MessageRecords => {
  const communicationMessages = Object.entries(data);

  const messageDays = communicationMessages.map((item) => {
    const [days, message] = item;
    const range = days.split('-');

    if (range.length === 1) {
      return [{ [range[0]]: message }];
    }

    const start = parseInt(range[0]);
    const end = parseInt(range[1]);

    const messageArray = [];

    for (let i = start; i <= end; i++) {
      messageArray.push({ [i]: message });
    }

    return messageArray;
  });

  const result = flatten(messageDays).reduce((prev, curr) => {
    return { ...prev, ...curr };
  }, {});

  return result;
};

const communications: Communication = {
  [EncounterService.TCP]: createMassageRecords({
    '1': 'TCPWelcomeMessage',
    '7': 'TCPCollectionDay8',
    '30': 'TCPCollectionDischarge',
  }),
  [EncounterService.HospitalAtHome]: {},
  [EncounterService.Arthroplasty]: {},
  [EncounterService.HealingHands]: {},
  [EncounterService.InHospitalMonitoring]: {},
  [EncounterService.RemotePatientMonitoring]: {},
  [EncounterService.HospitalAtHomeUpgrade]: {},
  [EncounterService.SPHERE]: {},
};

type RuleName = `${number}-${EncounterService}`;

const resolvers: SuccessResolvers<
  RuleName,
  MessageRecords,
  CommunicationParams
> = Object.entries(communications).reduce(
  (prev, curr) => {
    const [serviceType, messageDays] = curr;

    const day = Object.entries(messageDays);

    const ruleNames = day.map((item) => {
      const [rule, message] = item;
      const ruleName = `${rule}-${serviceType}` as RuleName;

      const resolver = resolveConstant({ [rule]: message });
      const res = [ruleName, resolver];
      return res;
    });

    const result = Object.fromEntries(ruleNames);

    return { ...prev, ...result };
  },
  {} as SuccessResolvers<RuleName, MessageRecords, CommunicationParams>,
);

export const getRuleName = ({
  dayOfAdmission,
  admissionService,
}: CommunicationParams): RuleName => {
  return `${dayOfAdmission}-${admissionService}`;
};

export const getMessage = compose<
  MessageRecords,
  CommunicationParams,
  RuleName
>({
  getRuleName,
  defaultAssertions: [],
  resolvers,
  defaultResolver: resolveConstant({ 0: undefined }),
});
